/* eslint-disable no-param-reassign */
import React from 'react';

import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';

import FormContainer from '../../../FormContainer';
import MortgageeForm from '../MortgageeForm';
import Documents from './Documents';
import Bills from './Bills';
import ActionsMenu from '../../../../ActionsMenu';

import useInsuranceForm from '../../../../../forms/loan/insurance';
import InsuranceService from '../../../../../services/InsuranceService';
import { useUiContext } from '../../../../../context/UiContext';
import useMortgageeForm from '../../../../../forms/loan/mortgagee';
import getStatusBadgeColor from './helpers/getStatusBadgeColor';
import { INSURANCE_STATUSES } from '../../../../../constants/formOptions';
import useInsuranceActions from './hooks/useInsuranceActions';

export default function InsuranceForm({
  isVisible,
  removePolicy,
  insurance,
  loanId,
  readOnly,
  refetchLoan,
  isEdit,
  refetchInsurance,
  lenderId,
}) {
  const { showToast } = useUiContext();

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const isUnpaid =
    !insurance?.bill_id ||
    !insurance?.bill_id?.length ||
    insurance?.bill_id?.every?.((billId) => !billId) ||
    insurance?.bill_id[0]?.status !== 'paid';

  const {
    mutateAsync: createInsuranceMutation,
    isPending: isCreatingInsurance,
  } = useMutation({
    mutationFn: async (formValues) => {
      const data = await InsuranceService.createInsurance({
        formValues,
        loanId,
      });

      return data;
    },
    onSuccess: () => {
      showToast({
        text: 'Success',
        type: 'success',
      });
      queryClient.invalidateQueries(['insurances', loanId]);
      navigate(`/loans/${loanId}?tab=Insurances`);
    },
    onError: () =>
      showToast({
        text: 'Something went wrong',
        type: 'error',
      }),
  });

  const mortgageeForm = useMortgageeForm(
    () => {},
    insurance?.mortgagees && insurance.mortgagees.length ? insurance : null
  );

  const { mutate: updateInsurance, isPending: isUpdatingInsurance } =
    useMutation({
      mutationFn: async ({ formValues }) => {
        const data = await InsuranceService.updateInsurance({
          formValues,
          insuranceId: insurance?.id,
        });

        // eslint-disable-next-line consistent-return
        return data;
      },
      onSuccess: (data, { noRedirect }) => {
        showToast({
          text: 'Success',
          type: 'success',
        });

        if (loanId) {
          queryClient.invalidateQueries(['insurances', loanId]);
          if (!noRedirect) {
            navigate(`/loans/${loanId}?tab=Insurances`);
          }
        } else if (lenderId) {
          queryClient.invalidateQueries(['lenderInsurances', lenderId]);
          if (!noRedirect) {
            navigate(`/lenders/${lenderId}?tab=Insurances`);
          }
        }
      },
      onError: () =>
        showToast({
          text: 'Something went wrong',
          type: 'error',
        }),
    });

  const updateInsuranceWithMortageesValidityCheck = async (formValues) => {
    mortgageeForm.formik.handleSubmit();

    const errors = await mortgageeForm.formik.validateForm();

    if (!errors.mortgagees) {
      const mortgageesWithPostions = mortgageeForm.formik.values.mortgagees.map(
        (mortgagee, index) => ({
          ...mortgagee,
          position: index + 1,
        })
      );

      updateInsurance({
        formValues: { ...formValues, mortgagees: mortgageesWithPostions },
      });
    }
  };

  const insuranceForm = useInsuranceForm(
    isEdit
      ? updateInsuranceWithMortageesValidityCheck
      : createInsuranceMutation,
    isEdit ? insurance : null,
    loanId,
    readOnly
  );

  const actions = useInsuranceActions({
    insurance,
    isUnpaid,
    isEdit,
    readOnly,
    formValues: insuranceForm.formik.values,
    refetchLoan,
    refetchInsurance,
    loanId,
    removePolicy,
  });

  return (
    <div className="relative p-7 col-span-full sm:col-span-6 xl:col-span-4 bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 mb-10">
      <div>
        <FormContainer
          isVisible={isVisible}
          form={insuranceForm}
          noSubmit
          readOnly={readOnly}
          noBillComId
          headerRight={
            isEdit && (
              <ActionsMenu
                actions={actions}
                badgeColor={getStatusBadgeColor(insurance?.status)}
                badgeText={
                  INSURANCE_STATUSES.find(
                    (stat) => stat.value === insurance?.status
                  )?.label
                }
              />
            )
          }
        />
      </div>
      <div className="mt-8">
        <MortgageeForm readOnly={readOnly} form={mortgageeForm} />
      </div>
      {!readOnly && (
        <button
          onClick={() => {
            insuranceForm.formik.handleSubmit();
          }}
          disabled={isCreatingInsurance || isUpdatingInsurance}
          type="button"
          className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mt-10 mb-12"
        >
          Submit
        </button>
      )}
      {isEdit && loanId && (
        <>
          <div
            className={classNames(
              'mb-10 shadow-lg border border-slate-200 dark:border-slate-700 px-4 py-2 cursor-pointer',
              { 'mt-10': readOnly }
            )}
          >
            <Documents
              loanId={loanId}
              insurance={insurance}
              refetchLoan={refetchLoan}
              refetchInsurance={refetchInsurance}
              readOnly={readOnly}
            />
          </div>
          <div className="shadow-lg border border-slate-200 dark:border-slate-700 px-4 py-2 cursor-pointer">
            <Bills
              loanId={loanId}
              updateInsuranceMutation={updateInsurance}
              insurance={insurance}
              readOnly={readOnly}
              refetchInsurance={refetchInsurance}
            />
          </div>
        </>
      )}
    </div>
  );
}
